import React, { useState } from 'react';
import Alert from 'components/styleguide/alert/Alert';
import Button from 'components/styleguide/button/Button';
import Checkbox from 'components/styleguide/checkbox/Checkbox';
import SelectField from 'components/styleguide/select_field/SelectField';
import Textarea from 'components/styleguide/textarea/Textarea';
import { displayPct, effectiveScores, gateOutcome } from './utils/curationScoring';
import { useCurationReview } from './state/CurationReviewContext';
import { useSubmitDecision } from './state/useSubmitDecision';

const styles = require('./DecisionPanel.module.scss');

export interface Props {
  canRequestHandpicked: boolean;
}

// The final, always-manual decision, pinned in the sidebar. Approve submits
// to the employer; reject expands the reason and message fields.
export default function DecisionPanel({ canRequestHandpicked }: Props) {
  const { dispatch, experienceMax, experienceMin, rejectionReasons, state } = useCurationReview();
  const submit = useSubmitDecision();
  const [rejecting, setRejecting] = useState(false);

  const outcome = gateOutcome(state.gateChecks);
  const scores = effectiveScores(
    state.payload,
    state.skillStates,
    state.directOverrides,
    experienceMin,
    experienceMax,
  );

  if (state.submitted) {
    return (
      <div className={styles['ui-decision']}>
        <span className={styles['ui-decision__label']}>Decision</span>
        <Alert message="Curation submitted." variant="success" />
      </div>
    );
  }

  const reason = state.decision.reason;
  const reasonOption = rejectionReasons.find((option) => option.value === reason) || null;
  const submitting = state.submitStatus === 'submitting';
  const canApprove = state.gateConfirmed && outcome.canContinue && scores.overall !== null;

  const approve = () => submit({ decision: 'approve' });

  const reject = () => {
    if (!reason) return;
    submit({
      decision: 'reject',
      rejection_candidate_message: state.decision.candidateMessage,
      rejection_landing_message: state.decision.internalNote,
      rejection_reason: reason,
      rejection_reason_other: state.decision.reasonOther,
    });
  };

  return (
    <div className={styles['ui-decision']}>
      <span className={styles['ui-decision__label']}>Decision</span>

      <p className={styles['ui-decision__summary']}>
        {!state.gateConfirmed
          ? 'Complete the eligibility checks to unlock the decision.'
          : scores.overall === null
            ? 'No match score yet.'
            : `${displayPct(scores.overall)} · ${
                scores.category === 'qualified' ? 'Qualified candidate' : 'Probably not a match'
              }`}
      </p>

      {canRequestHandpicked && (
        <Checkbox
          checked={state.handpicked}
          context="neutral"
          label="Handpick for the employer"
          onChange={(event) => dispatch({ type: 'SET_HANDPICKED', value: event.target.checked })}
        />
      )}

      {state.submitError && <Alert message={state.submitError} variant="error" />}

      {rejecting && (
        <div className={styles['ui-decision__rejection']}>
          <SelectField
            label="Rejection reason"
            onChange={(option: { label: string; value: string } | null) =>
              dispatch({ type: 'SET_DECISION', value: { reason: option ? option.value : null } })
            }
            options={rejectionReasons}
            value={reasonOption}
          />
          {reason === 'other' && (
            <Textarea
              label="Reason details"
              onChange={(event) =>
                dispatch({ type: 'SET_DECISION', value: { reasonOther: event.target.value } })
              }
              required
              rows={2}
            />
          )}
          <Textarea
            hint="Sent to the candidate with the rejection email. Optional."
            label="Message to the candidate"
            onChange={(event) =>
              dispatch({ type: 'SET_DECISION', value: { candidateMessage: event.target.value } })
            }
            rows={3}
          />
        </div>
      )}

      <div className={styles['ui-decision__actions']}>
        {!rejecting && (
          <Button
            className={styles['ui-decision__approve']}
            context="talent"
            disabled={!canApprove || submitting}
            fullWidth
            iconLeft="check"
            onClick={approve}
            size="S"
            spinner={submitting}
            type="primary"
          >
            Approve &amp; send to employer
          </Button>
        )}
        {rejecting ? (
          <>
            <Button
              className={styles['ui-decision__confirm-reject']}
              context="semantic"
              disabled={!reason || (reason === 'other' && !state.decision.reasonOther) || submitting}
              fullWidth
              onClick={reject}
              size="S"
              spinner={submitting}
              type="primary"
            >
              Confirm rejection
            </Button>
            <Button context="neutral" fullWidth onClick={() => setRejecting(false)} size="S" type="terceary">
              Back
            </Button>
          </>
        ) : (
          <Button
            className={styles['ui-decision__reject']}
            context="semantic"
            fullWidth
            iconLeft="x"
            onClick={() => setRejecting(true)}
            size="S"
            type="secondary"
          >
            Reject application
          </Button>
        )}
      </div>
    </div>
  );
}
